import { Component } from '@verndale/core';
import { createFocusTrap } from 'focus-trap';
import BookNow from './bookNow';
import anime from 'animejs';
import { breakpoints } from '../helpers/constants';

import InteractiveMap from './interactiveMap';

class PrimaryNavigation extends Component {
  setupDefaults() {
    this.runningAnimation = false;
    this.headerFixedBookNowOffset = 1000;
    this.dom = {
      html: document.querySelector('html'),
      body: document.querySelector('body'),
      //Primary Navigation elements
      controlsContainer: this.el.querySelector('.primary-navigation__controls'),
      controlsButtons: () =>
        this.el.querySelectorAll('.primary-navigation__controls > *'),
      searchTrigger: this.el.querySelector(
        '.primary-navigation__search__trigger'
      ),
      closeTrigger: this.el.querySelector('.primary-navigation__close-trigger'),
      interactiveMap: this.el.querySelector('.interactive-map'),
      interactiveMapNavigationItems: [
        ...this.el.querySelectorAll('.interactive-map__navigation-link')
      ],
      interactiveMapInner: this.el.querySelector(
        '.interactive-map__container .interactive-map__map-inner'
      ),
      navigationItems: [
        ...this.el.querySelectorAll('.primary-navigation-items__item')
      ],
      utilityNavigationDivider: this.el.querySelector(
        '.primary-navigation-items-container > .primary-navigation-items-divider'
      ),
      utilityNavigationItems: [
        ...this.el.querySelectorAll('.primary-navigation-utility ul > li')
      ],
      // Header elements
      openTrigger: document.querySelector(
        '.header__primary-navigation__trigger'
      ),
      bookNow: document.querySelector('.header__book-now__container'),
      header: document.querySelector('.header'),
      shortcutsContainer: document.querySelector('.header__expanded-shortcuts'),
      footer: document.querySelector('footer')
    };
    this.focusTrap = createFocusTrap(this.el, {
      initialFocus: this.dom.closeTrigger
    });
    this.setupWeatherTrigger();
    this.setupBookNow();
    this.setupMenuHoverSpans();
  }

  addListeners() {
    // Open and Close navigation
    this.dom.openTrigger.addEventListener('click', e =>
      this.openPrimaryNavigation(e)
    );
    this.dom.closeTrigger.addEventListener('click', () =>
      this.closePrimaryNavigation()
    );
    this.dom.searchTrigger.addEventListener('click', () => {
      this.closePrimaryNavigation(() => {
        document.dispatchEvent(new Event('openSearch'));
      });
    });

    this.el.addEventListener('keydown', e => this.handleKeyEvents(e));
    window.addEventListener('scroll', () => this.fixHeaderBookNow());
  }

  fixHeaderBookNow() {
    const footerOffset = this.dom.footer.offsetTop - 20;
    const bookNowOffsetBottom = this.dom.bookNow.offsetHeight + 20;

    if (window.innerWidth < breakpoints.tabletLandscape) return;
    const shouldBeFixed =
      scrollY > this.headerFixedBookNowOffset ||
      this.dom.body.style.position === 'fixed';
    const shouldStopBeforeFooter =
      bookNowOffsetBottom + scrollY >= footerOffset;
    this.dom.bookNow.classList.toggle('fixed', shouldBeFixed);
    this.dom.bookNow.classList.toggle(
      'fixed-before-footer',
      shouldStopBeforeFooter
    );
    if (shouldStopBeforeFooter) {
      this.dom.bookNow.style.top = `${
        footerOffset - 20 - this.dom.bookNow.offsetHeight
      }px`;
    } else {
      this.dom.bookNow.removeAttribute('style');
    }
  }

  setHtmlOverflow(value) {
    this.dom.html.style.overflowY = value;
  }

  setupWeatherTrigger() {
    if (!this.dom.shortcutsContainer) {
      this.dom.header.classList.remove('header-with-shortcuts');
    }
  }

  setupBookNow() {
    // Clone header book now element
    const bookNow = this.dom.bookNow.cloneNode(true);
    bookNow.classList.remove('header__book-now__container');
    bookNow.classList.add(
      'primary-navigation__book-now__container',
      'on-black'
    );
    // Switch from primary to secondary class
    const bookNowButton = bookNow.querySelector('.btn--primary');
    bookNowButton.classList.remove('btn--primary');
    bookNowButton.classList.add('btn--secondary');
    // Create new BookNow instance
    this.bookNow = new BookNow(bookNow, false, true);
    // Prepend bookNow to the primary navigation controls
    this.dom.controlsContainer.prepend(bookNow);
  }

  primaryNavigationDesktopAnimation({
    direction = 'normal',
    onComplete = null
  }) {
    if (this.runningAnimation) return;
    this.runningAnimation = true;
    const timeline = anime.timeline({
      easing: 'easeInOutQuad',
      direction,
      complete: () => onComplete && onComplete()
    });
    /**
     * Grow element container
     */
    timeline.add(
      {
        targets: this.el,
        width: ['0%', '100%'],
        duration: 400
      },
      0
    );
    /**
     * Fade in buttons
     */
    timeline.add(
      {
        targets: this.dom.controlsButtons(),
        opacity: [0, 1],
        translateX: ['10%', '0%'],
        duration: 300,
        delay: anime.stagger(100, { direction: 'reverse' }),
        complete: () => {
          this.bookNow.recalculatePopupHeight();
          this.bookNow.el.removeAttribute('style');
        }
      },
      66
    );
    // Run primary and utility nav animations
    this.leftSideNavsDesktopAnimations(timeline, 200);
    // Run interactive map animations
    this.interactiveMapDesktopAnimation(timeline, 332);
    this.featuredNavigationDesktopAnimation(timeline, 466);
  }

  featuredNavigationDesktopAnimation(timeline, start) {
    const duration = 132;
    const offset = 66;
    this.dom.interactiveMapNavigationItems.forEach((featuredNavItem, index) => {
      timeline.add(
        {
          targets: featuredNavItem,
          opacity: [0, 1],
          translateX: ['12%', '0%'],
          duration
        },
        index === 0 ? start : start + (duration - offset) * index
      );
    });
  }

  interactiveMapDesktopAnimation(timeline, start) {
    timeline.add(
      {
        targets: this.dom.interactiveMapInner,
        opacity: [0, 1],
        height: ['0%', '100%'],
        duration: 200
      },
      start
    );
  }

  leftSideNavsDesktopAnimations(timeline, start) {
    this.dom.navigationItems.forEach((navigationItem, index) => {
      const mainLink = navigationItem.querySelector(
        '.primary-navigation-items-main-link'
      );
      const divider = navigationItem.querySelector(
        '.primary-navigation-items-divider'
      );
      const hasChildLinks = navigationItem.querySelector(
        '.primary-navigation-items-child-list'
      );
      // Divider scalesX
      timeline.add(
        {
          targets: divider,
          opacity: [0, 0.5],
          scaleX: ['0', '100%'],
          duration: 150
        },
        index === 0 ? start : '-=232'
      );
      // Main link fades in
      timeline.add(
        {
          targets: mainLink,
          opacity: [0, 1],
          translateX: ['5%', '0%'],
          duration: 150
        },
        '-=120'
      );
      // Animate child links if exists
      if (hasChildLinks) {
        const oddChildLinks = [
          ...navigationItem.querySelectorAll(
            '.primary-navigation-items-child-link:nth-child(odd)'
          )
        ];
        const evenChildLinks = [
          ...navigationItem.querySelectorAll(
            '.primary-navigation-items-child-link:nth-child(even)'
          )
        ];
        // Fade in odd child links first
        oddChildLinks.forEach((oddChildLink, index) => {
          timeline.add(
            {
              targets: oddChildLink,
              opacity: ['0', '100%'],
              translateX: ['5%', '0%'],
              duration: 150
            },
            index === 0 ? '-=160' : '-=140'
          );
        });
        // Fade in even child links after odd links animation was completed
        evenChildLinks.forEach((eventChildLink, index) => {
          timeline.add(
            {
              targets: eventChildLink,
              opacity: ['0', '100%'],
              translateX: ['5%', '0%'],
              duration: 150
            },
            '-=100'
          );
        });
      }
    });
    // Utility nav divider scaleX
    timeline.add(
      {
        targets: this.dom.utilityNavigationDivider,
        opacity: [0, 0.5],
        scaleX: ['0', '100%'],
        duration: 150
      },
      '-=120'
    );
    // Fade in utility nav items
    timeline.add(
      {
        targets: this.dom.utilityNavigationItems,
        opacity: ['0', '100%'],
        translateX: ['5%', '0%'],
        duration: 150,
        delay: anime.stagger(120)
      },
      '-=120'
    );
  }

  openPrimaryNavigation() {
    if (window.innerWidth < breakpoints.tabletLandscape) return;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    this.setHtmlOverflow('hidden');
    this.el.classList.add('active');
    this.focusTrap.activate();
    this.primaryNavigationDesktopAnimation({
      onComplete: () => {
        this.runningAnimation = false;
      }
    });

    if (!this.interactiveMap) {
      this.interactiveMap = new InteractiveMap(this.dom.interactiveMap);
    }
  }

  closePrimaryNavigation(onComplete) {
    if (window.innerWidth < breakpoints.tabletLandscape) return;
    this.focusTrap.deactivate();
    this.primaryNavigationDesktopAnimation({
      direction: 'reverse',
      onComplete: () => {
        this.runningAnimation = false;
        this.el.classList.remove('active');
        this.setHtmlOverflow('auto');

        if (typeof onComplete === 'function') {
          onComplete();
        }
      }
    });
  }

  handleKeyEvents(e) {
    if (e.key === 'Escape') {
      this.closePrimaryNavigation();
    }
  }

  setupMenuHoverSpans() {
    //Create and append container element
    const container = document.createElement('div');
    container.classList.add('menu-trigger-animated');
    this.dom.openTrigger.append(container);
    // Create and append animated elements
    [...new Array(3)].forEach(() => {
      const element = document.createElement('div');
      element.classList.add('menu-trigger-animated__item');
      container.append(element);
    });
  }
}

export default PrimaryNavigation;
